import { EventStatus } from "@finbackoffice/enums";
import {
  ICategoryFeedResponse,
  IEventFeedResponse,
  ISportFeedResponse,
  ITopLeagueFeedResponse,
  ITournamentFeedResponse,
} from "./feed.storage.types";
import { mergeMarkets } from "./utils";
import {
  IFeedResponse,
  IFeedResponseSport,
  IFeedResponseCategory,
  IFeedResponseTournament,
  IFeedResponseEvent,
} from "./feed.server.types";

export type ISearchOptions = {
  include_empty?: boolean;
};

export type ISearchEventOptions = {
  statuses_to_exclude?: EventStatus[];
};

export class FeedStorage<
  TSportFeedResponse extends ISportFeedResponse = ISportFeedResponse,
  TCategoryFeedResponse extends ICategoryFeedResponse = ICategoryFeedResponse,
  TTournamentFeedResponse extends ITournamentFeedResponse = ITournamentFeedResponse,
  TTopLeagueFeedResponse extends ITopLeagueFeedResponse = ITopLeagueFeedResponse,
  TEventFeedResponse extends IEventFeedResponse = IEventFeedResponse,
> {
  constructor(
    private STATUSES_TO_DELETE: EventStatus[],
    private debug = false,
  ) {}

  private SPORTS: Map<number, TSportFeedResponse> = new Map();
  private CATEGORIES: Map<number, TCategoryFeedResponse> = new Map();
  private TOURNAMENTS: Map<number, TTournamentFeedResponse> = new Map();
  private EVENTS: Map<number, TEventFeedResponse> = new Map();

  private warn(message: string) {
    if (this.debug) {
      console.warn(message);
    }
  }

  public update(response: IFeedResponse) {
    this.updateSports(response.sports);
  }

  public getSports(options?: ISearchOptions): TSportFeedResponse[] {
    const sports = Array.from(this.SPORTS.values());
    if (options?.include_empty) {
      return sports;
    }

    return sports.filter((s) => s.games_count > 0);
  }

  public getSport(id: number): TSportFeedResponse | undefined {
    return this.SPORTS.get(id);
  }

  public getCategories(sportId: number, options?: ISearchOptions): TCategoryFeedResponse[] {
    const sport = this.SPORTS.get(sportId);
    const categories: TCategoryFeedResponse[] = [];
    const include_empty = !!options?.include_empty;

    if (!sport) {
      this.warn(`Unable to find sport with id ${sportId}`);
      return [];
    }

    for (const category_id of sport.categories) {
      const category = this.CATEGORIES.get(category_id);
      if (!category) {
        this.warn(`Unable to find category with id ${category_id}`);
        continue;
      }
      if (include_empty || category.games_count > 0) {
        categories.push(category);
      }
    }

    return categories;
  }

  public getCategory(id: number): TCategoryFeedResponse | undefined {
    return this.CATEGORIES.get(id);
  }

  public getTournaments(categoryId: number, options?: ISearchOptions): TTournamentFeedResponse[] {
    const category = this.CATEGORIES.get(categoryId);
    const tournaments: TTournamentFeedResponse[] = [];
    const include_empty = !!options?.include_empty;

    if (!category) {
      this.warn(`Unable to find category with id ${categoryId}`);
      return [];
    }

    for (const tournament_id of category.tournaments) {
      const tournament = this.TOURNAMENTS.get(tournament_id);
      if (!tournament) {
        this.warn(`Unable to find tournament with id ${tournament_id}`);
        continue;
      }
      if (include_empty || tournament.games_count > 0) {
        tournaments.push(tournament);
      }
    }

    return tournaments;
  }

  public getTopLeagues(): TTopLeagueFeedResponse[] {
    const topLeagues: ITopLeagueFeedResponse[] = [];
    for (const sport of this.SPORTS.values()) {
      for (const category_id of sport.categories) {
        const category = this.CATEGORIES.get(category_id);
        if (!category) {
          continue;
        }
        for (const tournament_id of category.tournaments) {
          const tournament = this.TOURNAMENTS.get(tournament_id);
          if (tournament && tournament.is_top && tournament.games_count) {
            topLeagues.push({
              ...tournament,
              sport_slug: sport.slug,
              sport_id: sport.id,
              sport_name: sport.name,
              category_id: category.id,
              category_name: category.name,
            });
          }
        }
      }
    }

    // @ts-expect-error: fixme
    return topLeagues.sort((a, b) => a.top_index - b.top_index) as unknown as TTopLeagueFeedResponse[];
  }

  public getTournament(id: number): TTournamentFeedResponse | undefined {
    return this.TOURNAMENTS.get(id);
  }

  public getEvents(tournamentId: number, options?: ISearchEventOptions): TEventFeedResponse[] {
    const tournament = this.TOURNAMENTS.get(tournamentId);
    const events: TEventFeedResponse[] = [];
    const statuses_to_exclude = options?.statuses_to_exclude || [EventStatus.Settled];

    if (!tournament) {
      this.warn(`Unable to find tournament with id ${tournamentId}`);
      return [];
    }

    for (const event_id of tournament.events) {
      const event = this.EVENTS.get(event_id);
      if (!event) {
        this.warn(`Unable to find event with id ${event_id}`);
        continue;
      }
      if (statuses_to_exclude.indexOf(event.status) === -1) {
        events.push(event);
      }
    }

    return events;
  }

  public getEventsBySportId(id: number, options?: ISearchEventOptions): TEventFeedResponse[] {
    const sport = this.SPORTS.get(id);
    const statuses_to_exclude = options?.statuses_to_exclude || [EventStatus.Settled];
    const results: TEventFeedResponse[] = [];

    if (!sport) {
      return results;
    }

    for (const categoryId of sport.categories) {
      const category = this.CATEGORIES.get(categoryId);

      if (!category) {
        continue;
      }

      for (const tournamentId of category.tournaments) {
        const tournament = this.TOURNAMENTS.get(tournamentId);

        if (!tournament) {
          continue;
        }

        for (const eventId of tournament.events) {
          const event = this.EVENTS.get(eventId);

          if (!event) {
            continue;
          }

          if (statuses_to_exclude.indexOf(event.status) === -1) {
            results.push(event);
          }
        }
      }
    }

    return results;
  }

  public getEvent(id: number): IEventFeedResponse | undefined {
    return this.EVENTS.get(id);
  }

  public clear() {
    this.SPORTS.clear();
    this.TOURNAMENTS.clear();
    this.CATEGORIES.clear();
    this.EVENTS.clear();
  }

  private updateSports(sports: IFeedResponseSport[]) {
    sports.forEach((s) => {
      const sport = this.SPORTS.get(s.id);
      const categories = [...new Set([...(sport?.categories ?? []), ...s.categories.map((c) => c.id)])];

      this.SPORTS.set(s.id, {
        ...s,
        categories,
      } as TSportFeedResponse);
      this.updateCategories(s.categories, s.id);
    });
  }

  private updateCategories(categories: IFeedResponseCategory[], sport_id: number) {
    categories.forEach((c) => {
      const category = this.CATEGORIES.get(c.id);
      const tournaments = [...new Set([...(category?.tournaments ?? []), ...c.tournaments.map((t) => t.id)])];
      this.CATEGORIES.set(c.id, {
        ...c,
        tournaments,
      } as TCategoryFeedResponse);
      this.updateTournaments(c.tournaments, sport_id, c.id);
    });
  }

  private updateTournaments(tournaments: IFeedResponseTournament[], sport_id: number, category_id: number) {
    tournaments.forEach((t) => {
      const tournament = this.TOURNAMENTS.get(t.id);
      const events = [...new Set([...(tournament?.events ?? []), ...t.events.map((e) => e.id)])];
      this.TOURNAMENTS.set(t.id, {
        ...t,
        events,
      } as TTournamentFeedResponse);
      this.updateEvents(t.events, sport_id, category_id, t.id);
    });
  }

  private updateEvents(events: IFeedResponseEvent[], sport_id: number, category_id: number, tournament_id: number) {
    events.forEach((event) => {
      if (this.STATUSES_TO_DELETE.indexOf(event.status) !== -1) {
        // delete event
        const tournament = this.TOURNAMENTS.get(tournament_id);
        if (!tournament) {
          this.warn(`Unable to find tournament with id ${tournament_id}`);
          return;
        }
        this.TOURNAMENTS.set(tournament.id, {
          ...tournament,
          events: tournament.events.filter((event_id) => event_id !== event.id),
        });
        this.EVENTS.delete(event.id);
        return;
      }

      if (!this.EVENTS.has(event.id)) {
        // create new record
        const sport = this.SPORTS.get(sport_id);
        const category = this.CATEGORIES.get(category_id);
        const tournament = this.TOURNAMENTS.get(tournament_id);

        this.EVENTS.set(event.id, {
          ...event,
          sport_id: sport!.id,
          sport_name: sport!.name,
          sport_slug: sport!.slug,
          category_id: category!.id,
          category_name: category!.name,
          tournament_id: tournament!.id,
          tournament_name: tournament!.name,
          tournament_is_top: tournament?.is_top ?? false,
          tournament_top_index: tournament?.top_index ?? 0,
        } as unknown as TEventFeedResponse);
        return;
      }

      if (this.EVENTS.has(event.id)) {
        // existing event, update markets only
        const cache = this.EVENTS.get(event.id)!;
        const markets = mergeMarkets(cache.markets, event.markets);
        this.EVENTS.set(event.id, {
          ...cache,
          ...event,
          markets,
        } as TEventFeedResponse);
        return;
      }
    });
  }
}
